import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, UserCog, Mail, ShieldOff, Archive, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  ["platform_owner", "Platform Owner"], ["admin", "Agency Admin"],
  ["ops_manager", "Manager"], ["agent", "Agent"], ["bpo", "Processor"],
  ["future", "Auditor"], ["vendor", "Vendor"],
];

function Modal({ title, onClose, children }) {
  return <div className="fixed inset-0 z-[90] grid place-items-center bg-black/60 p-4">
    <div className="w-full max-w-lg space-y-4 rounded-xl border bg-card p-5 shadow-xl">
      <div className="flex items-center justify-between"><div className="font-semibold">{title}</div>
        <button onClick={onClose}><X className="h-4 w-4" /></button></div>
      {children}
    </div>
  </div>;
}

function UserEditor({ item, agencies, onClose, onSaved }) {
  const [f, setF] = useState({ name: item.name || "", email: item.email || "", role: item.role || "agent",
    agency_id: item.agency_id || "", title: item.title || "", language: item.language || "en", active: item.active !== false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/platform/users/${item.id}`, f); toast.success("User updated"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return <Modal title={`Edit ${item.name}`} onClose={onClose}>
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
      <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
      <div><Label>Role</Label><select className="h-10 w-full rounded-md border bg-background px-3" value={f.role} onChange={(e) => set("role", e.target.value)}>
        {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
      <div><Label>Agency</Label><select className="h-10 w-full rounded-md border bg-background px-3" value={f.agency_id} onChange={(e) => set("agency_id", e.target.value)}>
        <option value="">Unassigned</option>{agencies.map((a) => <option key={a.id} value={a.id}>{a.display_name || a.name}</option>)}</select></div>
      <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
      <div><Label>Language</Label><select className="h-10 w-full rounded-md border bg-background px-3" value={f.language} onChange={(e) => set("language", e.target.value)}>
        <option value="en">English</option><option value="es">Spanish</option></select></div>
      <label className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active account</label>
    </div>
    <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button></div>
  </Modal>;
}

function VerifiedAction({ action, onClose, onDone }) {
  const [owner_password, setPassword] = useState("");
  const [mfa_code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post(`/platform/users/${action.user.id}/${action.kind}`, { owner_password, mfa_code });
      toast.success(action.kind === "reset-mfa" ? "Authenticator requirement reset" : "User archived"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return <Modal title={action.kind === "reset-mfa" ? `Reset MFA — ${action.user.name}` : `Archive ${action.user.name}`} onClose={onClose}>
    <p className="text-sm text-muted-foreground">Confirm this sensitive action with your Platform Owner password. Enter your authenticator code only if MFA is enabled on your owner account.</p>
    <div><Label>Platform Owner password</Label><Input type="password" value={owner_password} onChange={(e) => setPassword(e.target.value)} autoFocus /></div>
    <div><Label>Owner authenticator code (if enabled)</Label><Input value={mfa_code} onChange={(e) => setCode(e.target.value)} /></div>
    <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button>
      <Button variant={action.kind === "archive" ? "destructive" : "default"} onClick={run} disabled={busy || !owner_password}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}</Button></div>
  </Modal>;
}

export function PlatformUsers({ agencies, onActivation, initialAgencyId = "" }) {
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");
  const [agencyId, setAgencyId] = useState(initialAgencyId);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null);

  const load = () => api.get("/platform/users", { params: agencyId ? { agency_id: agencyId } : {} })
    .then(({ data }) => setRows(data)).catch((e) => { setRows([]); toast.error(formatApiErrorDetail(e?.response?.data?.detail)); });
  useEffect(load, [agencyId]);
  const filtered = useMemo(() => (rows || []).filter((u) => `${u.name} ${u.email} ${u.agency_name} ${u.role}`.toLowerCase().includes(query.toLowerCase())), [rows, query]);

  const reissue = async (u) => {
    try { const { data } = await api.post(`/platform/users/${u.id}/reissue-invite`); onActivation(data); toast.success(data.email_status?.sent ? "Invitation sent" : "New activation link created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const viewAgency = async (u) => {
    if (!u.agency_id) return;
    try { await api.post(`/platform/impersonation/start/${u.agency_id}`); window.location.assign("/"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return <div className="space-y-4" data-testid="platform-users">
    <div className="flex flex-wrap gap-2">
      <div className="relative min-w-64 flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input className="pl-9" placeholder="Search every user, email, role or agency" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
      <select className="h-10 rounded-md border bg-background px-3 text-sm" value={agencyId} onChange={(e) => setAgencyId(e.target.value)}>
        <option value="">All agencies</option>{agencies.map((a) => <option key={a.id} value={a.id}>{a.display_name || a.name}</option>)}</select>
    </div>
    {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin" /></div> :
      <div className="overflow-x-auto rounded-lg border bg-card"><table className="w-full min-w-[980px] text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="p-3">User</th><th>Agency</th><th>Role</th><th>Status</th><th>MFA</th><th>Last login</th><th className="pr-3">Controls</th></tr></thead>
        <tbody>{filtered.map((u) => <tr key={u.id} className="border-b">
          <td className="p-3"><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
          <td>{u.agency_name || "Unassigned"}</td><td>{ROLES.find(([v]) => v === u.role)?.[1] || u.role}</td>
          <td><Badge variant={u.active === false ? "destructive" : "secondary"}>{u.active === false ? "Inactive" : (u.invitation_status === "pending" ? "Invite pending" : "Active")}</Badge></td>
          <td>{u.mfa_enabled ? "Enabled" : "Off"}</td><td className="text-xs text-muted-foreground">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}</td>
          <td className="space-x-1 whitespace-nowrap pr-3">
            <Button size="sm" variant="outline" onClick={() => setEdit(u)} title="Edit user"><UserCog className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => reissue(u)} title="Reissue activation/password link"><Mail className="h-3.5 w-3.5" /></Button>
            {u.mfa_enabled && <Button size="sm" variant="outline" onClick={() => setAction({ kind: "reset-mfa", user: u })} title="Reset MFA"><ShieldOff className="h-3.5 w-3.5" /></Button>}
            {u.agency_id && <Button size="sm" variant="outline" onClick={() => viewAgency(u)} title="View this agency"><ExternalLink className="h-3.5 w-3.5" /></Button>}
            {u.role !== "platform_owner" && <Button size="sm" variant="outline" onClick={() => setAction({ kind: "archive", user: u })} title="Archive user"><Archive className="h-3.5 w-3.5" /></Button>}
          </td>
        </tr>)}</tbody>
      </table></div>}
    {edit && <UserEditor item={edit} agencies={agencies} onClose={() => setEdit(null)} onSaved={load} />}
    {action && <VerifiedAction action={action} onClose={() => setAction(null)} onDone={load} />}
  </div>;
}
